/* eslint-disable sonarjs/cognitive-complexity */
import { getStepIndexByName } from '../../helpers/processFlowHelper';
import { CoreHelper, LoggerHelper } from 'ff-shared-components-core';
import {
	ProcessFlowActionTypes,
	ProcessFlowChangeActions,
	ProcessFlowState,
	ProcessFlowGotoOptions,
	ProcessFlowActionType,
} from './types';

export const initialProcessFlowState: ProcessFlowState = {
	id: '',
	numberOfSteps: 0,
	steps: [],
	initialStepIndex: -1,
	currentStepIndex: -1,
	currentFlowUrl: '',
	action: undefined,
	history: [],
	restore: undefined,
};

const getHistory = (
	actionType: ProcessFlowActionType,
	state: ProcessFlowState,
	currentStepIndex: number,
	currentFlowUrl: string,
	gotoAction?: any
): any => {
	// LoggerHelper.log(
	// 	'processFlowReducer > getHistory',
	// 	actionType,
	// 	state,
	// 	currentStepIndex,
	// 	currentFlowUrl,
	// 	gotoAction
	// );

	if (state.steps.length === 0) {
		return [];
	}

	let history = state.history;
	const lastStep = state.history.length > 0 ? state.history[state.history.length - 1] : undefined;
	const definedGotoAction = gotoAction ? gotoAction : {};
	const { goToStepName: actionGoToStepName, options: actionOptions } = definedGotoAction;

	let { gotoFlowUrl: actionGotoFlowUrl } = definedGotoAction;

	// when actionGotoFlowUrl is not defined or string empty, set with current flow url
	actionGotoFlowUrl = !actionGotoFlowUrl || actionGotoFlowUrl === '' ? state.currentFlowUrl : actionGotoFlowUrl;

	if (actionType === ProcessFlowActionType.Init) {
		history = state.restore && state.restore.stepName ? history : [];
	} else if (actionType === ProcessFlowActionType.Goto) {
		const currentStepName = state.steps[state.currentStepIndex].name;
		const ignoreHistory = state.steps[state.currentStepIndex].ignoreHistory;

		// add step to history when,
		// last step of history is different to current step, and check if current step not defined to ignore history
		if (
			!ignoreHistory &&
			(!lastStep || lastStep.stepIndex !== state.currentStepIndex || lastStep.flowUrl !== state.currentFlowUrl) &&
			// to prevent loops (ex: step 1 -> step 2 -> step 1)
			(!lastStep || lastStep.stepIndex !== currentStepIndex || lastStep.flowUrl !== currentFlowUrl)
		) {
			history.push({
				stepIndex: state.currentStepIndex,
				stepName: currentStepName,
				flowUrl: state.currentFlowUrl,
			});
		}
	}

	// check last step is equal to current step to remove from history
	if (lastStep !== undefined && lastStep.stepIndex === currentStepIndex && lastStep.flowUrl === currentFlowUrl) {
		// LoggerHelper.log('processFlowReducer > getHistory > remove last step because is equal to current');

		history.pop();
	}

	if (actionOptions) {
		const clearAllHistory = actionOptions.clearAllHistory === undefined ? false : actionOptions.clearAllHistory;
		const clearStepHistory = actionOptions.clearStepHistory === undefined ? false : actionOptions.clearStepHistory;

		// check if is to clear all history
		if (clearAllHistory) {
			// LoggerHelper.log('processFlowReducer > getHistory > clear all history');

			history = [];
		}

		// check if is to clear step history
		// based on passed step, check if exists in history and if exists clear all steps to this goto step
		if (clearStepHistory) {
			// 1. find step in history
			const indexOfStep = history.findIndex(step => {
				return step.flowUrl === currentFlowUrl && step.stepIndex === currentStepIndex;
			});

			// 1.1. if exists, clear all steps to goto step
			if (indexOfStep >= 0) {
				history = history.slice(0, indexOfStep);
			}

			// LoggerHelper.log('processFlowReducer > getHistory > clear step history', indexOfStep);
		}
	}

	// LoggerHelper.log('processFlowReducer > getHistory', history);

	return history;
};

export const processFlowReducer: (state: ProcessFlowState | undefined, action: any) => ProcessFlowState = (
	state = initialProcessFlowState,
	action
) => {
	// LoggerHelper.log('processFlowReducer', state, action);

	switch (action.type) {
		case ProcessFlowActionTypes.INIT: {
			const {
				initialStepName: actionInitialStepName,
				flowUrl: actionFlowUrl,
				steps: actionSteps,
				flowId: actionFlowId,
			} = action.payload;

			const initialStepName =
				state.restore && state.restore.stepName ? state.restore.stepName : actionInitialStepName;

			const flowUrl = actionFlowUrl;

			const initialStepIndex = initialStepName ? getStepIndexByName(actionSteps, initialStepName) : 0;

			const history = getHistory(ProcessFlowActionType.Init, state, initialStepIndex, flowUrl);

			// LoggerHelper.log('processFlowReducer > init', state, action, initialStepIndex, history);

			return {
				...state,
				id: actionFlowId || '',
				numberOfSteps: (actionSteps && actionSteps.length) || 0,
				steps: actionSteps || [],
				initialStepIndex: CoreHelper.isNull(initialStepIndex) ? -1 : initialStepIndex,
				currentStepIndex: CoreHelper.isNull(initialStepIndex) ? -1 : initialStepIndex,
				currentFlowUrl: flowUrl,
				action: undefined,
				restore: undefined,
				history,
			};
		}
		case ProcessFlowActionTypes.BACK: {
			let history = state.history;

			let currentStepIndex = state.currentStepIndex;
			const currentFlowUrl = state.currentFlowUrl;
			let restore;

			if (history.length > 0) {
				const step = history.pop();

				if (step) {
					if (step.flowUrl !== currentFlowUrl) {
						restore = {
							flowUrl: step.flowUrl,
							stepName: step.stepName,
						};
					} else {
						currentStepIndex = step.stepIndex;
					}
				}
			}

			history = getHistory(ProcessFlowActionType.Back, state, currentStepIndex, currentFlowUrl);

			// LoggerHelper.log('processFlowReducer > back', state, currentStepIndex, restore, history);

			return {
				...state,
				currentStepIndex,
				currentFlowUrl,
				action: {
					type: ProcessFlowChangeActions.Back,
				},
				history,
				restore,
			};
		}
		case ProcessFlowActionTypes.GOTO: {
			const { gotoFlowUrl: gotoFlowUrlAction, goToStepName: goToStepNameAction } = action.payload;

			const gotoStepIndex = getStepIndexByName(state.steps, goToStepNameAction);

			let currentFlowUrl = state.currentFlowUrl;
			let currentStepIndex = state.currentStepIndex;
			let restore;

			if (gotoFlowUrlAction && gotoFlowUrlAction !== currentFlowUrl) {
				restore = {
					flowUrl: gotoFlowUrlAction,
					stepName: goToStepNameAction,
				};

				currentFlowUrl = gotoFlowUrlAction;
			} else if (gotoStepIndex >= 0 && gotoStepIndex < state.numberOfSteps) {
				currentStepIndex = gotoStepIndex;
			}

			const history = getHistory(
				ProcessFlowActionType.Goto,
				state,
				currentStepIndex,
				currentFlowUrl,
				action.payload
			);

			// LoggerHelper.log('processFlowReducer > goto', state, action, gotoStepIndex, currentStepIndex, history);

			return {
				...state,
				currentStepIndex,
				currentFlowUrl,
				action: {
					type: ProcessFlowChangeActions.Goto,
					payload: action.payload,
				},
				history,
				restore,
			};
		}
		case ProcessFlowActionTypes.ACTION_DONE: {
			return {
				...state,
				action: undefined,
			};
		}
		case ProcessFlowActionTypes.CLEAR: {
			const { flowId: actionFlowId } = action.payload;

			// only clear data when flowId passed is equal to flow id saved in store, otherwise ignore action
			if (actionFlowId === state.id) {
				return initialProcessFlowState;
			}

			return state;
		}
		default: {
			return state;
		}
	}
};

export default processFlowReducer;
